import { UpdateNodeBoundsCommand, UpdateTextCommand } from "@/commands/nodeCommands";
import { TransactionCommand } from "@/commands/transaction";
import { getAllSceneNodes } from "@/document/model/document";
import type { Paint } from "@/document/model/styles";
import {
  patchTextTypeStyleForInspector,
  textFillInspectorValue,
  textInspectorRange,
  textWithBoldOverride,
  type RichTextModel,
  type TypeStyle,
} from "@/document/model/text";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { scheduleTextBooleanOperandRefresh } from "@/editor/shell/shellBooleanRefresh";
import { boundsEqual, textEqual } from "@/editor/shell/shellSelection";
import { textBoundsForLayout, updateTextCharactersInDocument, warmTextFontBytes } from "@/editor/shell/shellTextHelpers";
import { documentStore, selectionStore, uiStore, viewStore } from "@/editor/store";
import { loadGoogleFontWeights } from "@/lib/GoogleFontLoader";
import type { RefLike } from "../editorCommandHost";
import type { EditorCommandHost } from "../editorCommandHost";

export interface TextHandlerDeps {
  textPreviewGenerationRef: RefLike<number>;
  onFontsResolved?: () => void;
}

export function updateTextNode(
  host: EditorCommandHost,
  nodeId: string,
  text: RichTextModel,
  message = "Updated text",
): void {
  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === nodeId);
  if (node?.type !== "text" || isNodeLockedForEditing(document, node.id) || textEqual(node.text, text)) {
    return;
  }

  void warmTextFontBytes(text).catch(error => {
    console.warn("Failed to warm text font bytes", error);
  });
  const nextBounds = textBoundsForLayout(node, text, Math.round(uiStore.zoom.getValue() * 10));
  const commands = [
    new UpdateTextCommand(nodeId, node.text, text),
    ...(boundsEqual(node.bounds, nextBounds) ? [] : [new UpdateNodeBoundsCommand(nodeId, node.bounds, nextBounds)]),
  ];
  const context = host.commandContext();
  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand(message, commands),
    context,
    { afterSelection: host.currentSelection() },
  );
  host.commit(context.document, message);
  scheduleTextBooleanOperandRefresh(host, context.document, nodeId);
}

export function updateTextCharacters(host: EditorCommandHost, nodeId: string, characters: string): void {
  if (isNodeLockedForEditing(documentStore.getDocument(), nodeId)) {
    return;
  }

  host.patchDocument(current => {
    const next = updateTextCharactersInDocument(current, nodeId, characters, Math.round(uiStore.zoom.getValue() * 10));
    if (next !== current) {
      scheduleTextBooleanOperandRefresh(host, next, nodeId);
    }
    return next;
  });
}

export function toggleTextBold(host: EditorCommandHost, nodeId: string, selectionStart: number, selectionEnd: number): void {
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
  if (node?.type !== "text" || isNodeLockedForEditing(documentStore.getDocument(), node.id)) {
    return;
  }

  updateTextNode(host, nodeId, textWithBoldOverride(node.text, selectionStart, selectionEnd), "Updated text style");
}

export function updateTextTypeStyle(host: EditorCommandHost, nodeId: string, patch: Partial<TypeStyle>, deps: TextHandlerDeps): void {
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
  if (node?.type !== "text" || isNodeLockedForEditing(documentStore.getDocument(), node.id)) {
    return;
  }

  const selection = selectionStore.getSelection();
  const textSelectionState = uiStore.textSelection.getValue();
  const textSelection = selection.mode === "textEdit" && textSelectionState?.nodeId === nodeId ? textSelectionState : undefined;
  const withDefaultStyle = patchTextTypeStyleForInspector(node.text, patch, textSelection ? { anchor: textSelection.anchor, focus: textSelection.focus } : undefined);
  clearTextTypeStylePreview(nodeId, deps);
  updateTextNode(host, nodeId, withDefaultStyle, "Updated text style");
}

export function previewTextTypeStyle(nodeId: string, patch: Partial<TypeStyle>, deps: TextHandlerDeps): void {
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
  if (node?.type !== "text" || isNodeLockedForEditing(documentStore.getDocument(), node.id)) {
    return;
  }

  const selection = selectionStore.getSelection();
  const textSelectionState = uiStore.textSelection.getValue();
  const textSelection = selection.mode === "textEdit" && textSelectionState?.nodeId === nodeId ? textSelectionState : undefined;
  const nextText = patchTextTypeStyleForInspector(node.text, patch, textSelection ? { anchor: textSelection.anchor, focus: textSelection.focus } : undefined);
  const generation = ++deps.textPreviewGenerationRef.current;
  viewStore.updateTextPreviews(current => {
    const next = new Map(current);
    next.set(nodeId, nextText);
    return next;
  });
  if (patch.fontFamily) {
    void loadGoogleFontWeights(patch.fontFamily).then(() => {
      if (generation !== deps.textPreviewGenerationRef.current) {
        return;
      }
      deps.onFontsResolved?.();
    });
  }
}

export function clearTextTypeStylePreview(nodeId: string | undefined, deps: TextHandlerDeps): void {
  deps.textPreviewGenerationRef.current += 1;
  viewStore.updateTextPreviews(current => {
    if (!nodeId) {
      return current.size === 0 ? current : new Map();
    }
    if (!current.has(nodeId)) {
      return current;
    }
    const next = new Map(current);
    next.delete(nodeId);
    return next;
  });
}

export function textFillForInspector(nodeId: string): Paint | "mixed" | undefined {
  const selection = selectionStore.getSelection();
  const textSelectionState = uiStore.textSelection.getValue();
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
  if (node?.type !== "text") {
    return undefined;
  }

  const textSelection = selection.mode === "textEdit" && textSelectionState?.nodeId === nodeId ? textSelectionState : undefined;
  const range = textInspectorRange(node.text, textSelection ? { anchor: textSelection.anchor, focus: textSelection.focus } : undefined);
  return textFillInspectorValue(node.text, range, node.style.fills.at(-1));
}
